import { Checkbox, Group, Select, Stack } from '@mantine/core';
import QuestionMarkTooltip from 'components/QuestionMarkTooltip';
import FormInputGroup from 'components/forms/FormInputGroup';
import { ChastiaConfigForm } from 'components/forms/remote-source/config/types';
import { forwardRef, useState } from 'react';

interface DocumentDeliveryItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  help?: string;
  keyboardHelp?: boolean;
  'data-hovered'?: boolean;
}

/** Document delivery choice with optional help inside the dropdown item. */
const DocumentDeliveryItem = forwardRef<HTMLDivElement, DocumentDeliveryItemProps>(
  ({ label, help, keyboardHelp, 'data-hovered': hovered, ...others }, ref) => (
    <Group ref={ref} {...others} data-hovered={hovered} aria-label={label} spacing={4} noWrap>
      <span>{label}</span>
      {help && (
        <QuestionMarkTooltip
          withinPortal
          multiline
          width={330}
          label={help}
          buttonLabel="Pomoc: Dokument s podpisovou doložkou"
          buttonTabIndex={-1}
          opened={keyboardHelp && hovered}
        />
      )}
    </Group>
  )
);
DocumentDeliveryItem.displayName = 'DocumentDeliveryItem';

interface WorkflowSettingsProps {
  form: ChastiaConfigForm;
}

/**
 * Workflow settings for revisions imported from and sent to Chastia.
 */
export default function WorkflowSettings({ form }: WorkflowSettingsProps) {
  const [keyboardHelp, setKeyboardHelp] = useState(false);
  return (
    <FormInputGroup groupTitle="Nastavenia workflow">
      <Stack spacing={12}>
        <Checkbox
          aria-label="Začínať revízne správy v stave Rozpracovaná"
          label={
            <Group spacing={4} noWrap>
              <span>Začínať revízne správy v stave Rozpracovaná</span>
              <QuestionMarkTooltip
                withinPortal
                multiline
                width={330}
                buttonLabel="Pomoc: Začínať revízne správy v stave Rozpracovaná"
                label="Nové revízne správy sa prijmú ako rozpracované. Pred vypracovaním musí byť pridelený technik, ktorý rozhodne termíny zariadení a vyberie šablónu. Po úspešnom prijatí správy sa zaradí potvrdenie prijatia pre Chastia. Potvrdenie začatia práce sa zaradí až po výbere šablóny."
              />
            </Group>
          }
          size="md"
          {...form.getInputProps('startRevisionsInProgress', { type: 'checkbox' })}
        />
        <Select
          label="Odosielanie dokumentov"
          maw={600}
          size="md"
          clearable={false}
          allowDeselect={false}
          data={[
            { value: 'unsigned', label: 'Odosielať nepodpísané dokumenty' },
            { value: 'signed', label: 'Odosielať podpísané dokumenty' },
            {
              value: 'unsigned-with-archive-clause',
              label: 'Odosielať nepodpísané dokumenty s pripojenou podpisovou doložkou',
              help: 'Do systému Chastia sa odošle jeden PDF súbor: nepodpísaná revízna správa s pripojenou podpisovou doložkou. Odošle sa po dokončení KEP podpisovania, keď je dostupná doložka.',
              keyboardHelp,
            },
          ]}
          itemComponent={DocumentDeliveryItem}
          styles={{ item: { whiteSpace: 'normal', wordBreak: 'normal' } }}
          {...form.getInputProps('documentDeliveryMode')}
          onKeyDown={() => setKeyboardHelp(true)}
          onPointerDown={() => setKeyboardHelp(false)}
          onDropdownClose={() => setKeyboardHelp(false)}
        />
      </Stack>
    </FormInputGroup>
  );
}
